import { useContact } from '@/context';
import {
  useDataLayerOneTrustConfirmationEvent,
  useDataLayerOneTrustDefaultEvent,
} from '@/hooks/useDataLayerEvent';
import { cleanObject } from '@/utils/objects';
import React from 'react';
import TagManager from 'react-gtm-module';
import { useStorageState } from 'react-storage-hooks';
import { useTriggerPortalEvent } from '@/features/shared/DataLayerLoginLogout/useTriggerPortalEvent';

export const useLogin = () => {
  const { contactInfo } = useContact();
  const dataLayerRef = React.useRef({});
  const dataLayerPortalPageRef = React.useRef({});
  const [isLoggedIn, setIsLoggedIn] = useStorageState(
    sessionStorage,
    'DATALAYER.IS_LOGGED_IN',
    false,
  );
  const [isOneTrustEventEnabled, setIsOneTrustEventEnabled] = React.useState(isLoggedIn);
  const [isPortalEventEnabled, setIsPortalEventEnabled] = React.useState(isLoggedIn);

  const { triggerPortalDataLayerEvent } = useTriggerPortalEvent();

  // Clear isLogged from storage
  React.useEffect(() => {
    setIsLoggedIn(false);
  }, [setIsLoggedIn]);

  /**
   * This effect is used to set the dataLayerRef.current object with the login event data.
   */
  React.useEffect(() => {
    const metadata = cleanObject({
      user_id: contactInfo?.contactId,
      login_status: 'logged_in',
      account_type: contactInfo?.accountType,
      registration_status: contactInfo?.portalRegistrationStatus,
      gen_beta_participant_status: Number(contactInfo?.betaParticipantStatus).toString(),
      portal_tracking_status: contactInfo?.portalPrivacyConsent?.toString(),
    });

    dataLayerRef.current = {
      event: 'trackEvent-accessEngagement',
      event_name: 'login',
      eventCategory: 'engagement',
      eventAction: 'login_success',
      eventLabel: 'SSO',
      method: 'SSO',
      ...metadata,
    };

    dataLayerPortalPageRef.current = {
      event: 'trackEventPortalPageLoad-accessEngagement',
      event_name: 'login',
      method: 'SSO',
      ...metadata,
    };
  }, [contactInfo]);

  /**
   * Use this effect to send the login event to GTM after OneTrustGroupsUpdated event is fired
   * when user accepts the privacy policy.
   */
  useDataLayerOneTrustConfirmationEvent(
    (_, unsubscribe = () => {}) => {
      if (isOneTrustEventEnabled) {
        TagManager.dataLayer({ dataLayer: dataLayerRef.current });
        setIsOneTrustEventEnabled(false);
      }

      unsubscribe();
    },
    { fireOnMount: true },
  );

  /**
   * Use this effect to send the login event to GTM after OneTrustGroupsUpdated event is fired
   * when user passes the privacy policy, only when consent is given
   */
  useDataLayerOneTrustDefaultEvent(
    (_, unsubscribe = () => {}) => {
      if (isPortalEventEnabled) {
        triggerPortalDataLayerEvent(dataLayerPortalPageRef.current);
        setIsPortalEventEnabled(false);
      }

      unsubscribe();
    },
    { fireOnMount: true },
  );
};
